import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

interface OAuthCallbackSuccessResult {
    token: string;
    accountId: string;
    displayName: string;
}

interface OAuthCallbackDeniedResult {
    reason: 'missing_role';
    requiredRoleId: string | null;
    requiredRoleName: string | null;
}

export type OAuthCallbackResult =
    | { status: 'success'; data: OAuthCallbackSuccessResult }
    | { status: 'denied'; data: OAuthCallbackDeniedResult }
    | { status: 'none' };

/**
 * What the server's sign-in redirect says, from the URL's query string and
 * fragment. A sign-in comes in the fragment, so its session token never
 * reaches a server log or a Referer; a denial comes in the query string.
 */
export function parseOAuthCallback(search: string, hash: string): OAuthCallbackResult {
    const query = new URLSearchParams(search);
    if (query.get('oauth')?.trim() === 'denied') {
        if (query.get('reason')?.trim() === 'missing_role') {
            return {
                status: 'denied',
                data: {
                    reason: 'missing_role',
                    requiredRoleId: query.get('requiredRoleId')?.trim() || null,
                    requiredRoleName: query.get('requiredRoleName')?.trim() || null,
                },
            };
        }

        return { status: 'none' };
    }

    const fragment = new URLSearchParams(hash.replace(/^#/, ''));
    const token = fragment.get('token')?.trim();
    const accountId = fragment.get('accountId')?.trim();
    const displayName = fragment.get('displayName')?.trim();
    if (fragment.get('oauth')?.trim() !== '1' || !token || !accountId || !displayName) {
        return { status: 'none' };
    }

    return {
        status: 'success',
        data: { token, accountId, displayName },
    };
}

export function useOAuthCallback(): OAuthCallbackResult {
    const { search, hash } = useLocation();

    return useMemo(() => parseOAuthCallback(search, hash), [search, hash]);
}
